// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";

import LoginButton from "../components/LoginButton";

import { theme } from "../../theme";
import { createStyleSheet } from "../utils/styles";
import welcomeImage from "../../assets/welcome_logo.png";
import { useSelector } from "react-redux";
import {
  selectCurrentOrganizationId,
  selectCurrentToken,
  setCredentials,
} from "../features/auth/authSlice";
import type { AuthState } from "../types/auth";
import { useAppDispatch } from "../features/store";
import { logEvent } from "../features/api/apiSlice";
import { useLazyGetProjectQuery, useListProjectsQuery } from "../features/data/projectApiSlice";
import { CREATE_PROJECT_WITHOUT_TEST_PATH, DASHBOARD_PATH } from "../utils/constants";
import { PROJECT_FLOW_QUERY_KEYS, PROJECT_FLOW_STEP_VALUES } from "../types/projects";
import { clearProject } from "../features/data/projectSlice";

const getGoogleClientID = () => {
  const host = window.location.hostname;
  const isProd = !host.includes(".staging.") && !host.includes("localhost");

  const clientId = isProd
    ? import.meta.env.VITE_GOOGLE_CLIENT_ID_PROD
    : import.meta.env.VITE_GOOGLE_CLIENT_ID_STAGING;

  if (!clientId) {
    console.error("Google Client ID is not defined in environment variables.");
    throw new Error("Google Client ID is not defined");
  }

  return `${clientId}.apps.googleusercontent.com`;
};

const Login = () => {
  const { t } = useTranslation("Login");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const clientId = getGoogleClientID();

  const { data: listProjectsData, isSuccess: isListProjectsSuccess } = useListProjectsQuery(
    undefined,
    {
      skip: !isLoggedIn,
    }
  );
  const [getProject] = useLazyGetProjectQuery();

  const onAuthSuccess = useCallback(
    (authData: AuthState) => {
      dispatch(setCredentials(authData));
      logEvent(dispatch, {
        event: "userLogin",
        data: { success: true },
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!currentOrganizationId || !isListProjectsSuccess) return;
    const projects = listProjectsData?.projects;
    dispatch(clearProject());
    if (!projects?.length) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.PROJECT_NAME);
      navigate(
        `${generatePath(CREATE_PROJECT_WITHOUT_TEST_PATH, {
          orgUid: currentOrganizationId,
        })}?${searchParams.toString()}`
      );
      return;
    }
    const projectUid = projects[0]?.project_uid;
    getProject({ projectUid })
      .unwrap()
      .then(response => {
        const test = response?.project?.rdts?.[0];
        if (!test) return;
        navigate(
          generatePath(DASHBOARD_PATH, {
            orgUid: currentOrganizationId,
            projectUid,
            testUid: test.rdt_uid,
          })
        );
      });
  }, [
    listProjectsData,
    currentOrganizationId,
    isListProjectsSuccess,
    navigate,
    getProject,
    dispatch,
  ]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leftPane}>
        <Box sx={styles.leftPaneInnerContainer}>
          <Typography variant="h3" textAlign="left">
            {t("welcome")}
          </Typography>
          <Typography variant="body1" textAlign="left" sx={{ color: theme.palette.text.secondary }}>
            {t("signinPrompt")}
          </Typography>
          <LoginButton
            googleClientId={clientId}
            onAuthSuccess={onAuthSuccess}
            data-testid="login-button"
          />
        </Box>
      </Box>
      <Box sx={styles.rightPane}>
        <Box sx={styles.heroContainer}>
          <Box component="img" src={welcomeImage} sx={styles.welcomeImage} alt="Welcome image" />
          <Typography variant="body2" textAlign="center" sx={styles.welcomePromptText}>
            {t("welcomePrompt")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column-reverse",
    },
  },
  leftPane: {
    height: "100%",
    width: theme.spacing(76),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
      width: "50%",
      padding: theme.spacing(2),
      height: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      width: "100%",
    },
  },
  leftPaneInnerContainer: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      width: "80%",
    },
  },
  rightPane: {
    backgroundColor: theme.palette.detailBackground.main,
    height: "100%",
    flex: 1,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      height: "100%",
      padding: theme.spacing(2),
    },
  },
  heroContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("sm")]: {
      flex: "none",
      width: "100%",
    },
  },
  welcomeImage: {
    maxWidth: "60%",
    maxHeight: "60%",
    aspectRatio: 1.5,
    width: "100%",
    height: "auto",
  },
  welcomePromptText: {
    marginTop: theme.spacing(4),
    width: "60%",
    [theme.breakpoints.down("sm")]: {
      width: "80%",
    },
  },
});

export default Login;
